import { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Skeleton,
  Menu,
  MenuItem,
} from '@mui/material';
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { categoriesApi, Category } from '../../api/categories';
import { useAuth } from '../../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCategoryName } from '../../utils/categoryUtils';
import i18n from '@/i18n';

interface SideMenuProps {
  open: boolean;
  onClose: () => void;
}

export default function SideMenu({ open, onClose }: SideMenuProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'zh', name: '繁體中文', flag: '🇭🇰' },
  ];

  useEffect(() => {
    fetchCategories();
  }, []); // Only call once when component mounts

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const data = await categoriesApi.getCategories();
      // Store all categories
      setAllCategories(data);
      // Filter only top-level categories (parentId is null) and active categories
      const topLevelCategories = data.filter(cat => cat.parentId === null && cat.isActive);
      setCategories(topLevelCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryClick = (category: Category) => {
    // Check if category has subcategories
    const categorySubcategories = allCategories.filter(cat => cat.parentId === category.id && cat.isActive);
    
    if (categorySubcategories.length > 0) {
      // Show subcategories
      setSelectedCategory(category);
      setSubcategories(categorySubcategories);
    } else {
      // Navigate to Categories page with category ID and name
      navigate(`/categories/${category.id}/${encodeURIComponent(getCategoryName(category, i18n.language))}`);
      onClose();
    }
  };

  const handleSubcategoryClick = (subcategory: Category) => {
    // Navigate to Categories page with subcategory ID and name
    navigate(`/categories/${subcategory.id}/${encodeURIComponent(getCategoryName(subcategory, i18n.language))}`);
    onClose();
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSubcategories([]);
  };

  const handleQuickLinkClick = (link: string) => {
    // Handle quick link navigation here
    if (link === 'new') {
      navigate('/new-products');
    } else if (link === 'sale') {
      navigate('/sale-products');
    } else if (link === 'brands') {
      navigate('/brands');
    }
    onClose();
  };

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchorEl(null);
  };

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    handleLanguageClose();
  };

  const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1300, // Higher than default MUI drawer z-index
        '& .MuiDrawer-paper': {
          width: '100%',
          height: '100%',
          backgroundColor: '#F8F6F3',
          display: 'flex',
          flexDirection: 'row',
          zIndex: 1300, // Ensure paper also has high z-index
        },
      }}
    >
      {/* CSS Animation for slide-in effect */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>

      {/* Sidebar Header */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 60,
          bgcolor: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          zIndex: 2,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        {/* Sign-in/Sign-up Text */}
        <Typography 
          fontWeight={700} 
          color="#333"
          fontSize={16}
          sx={{ 
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 }
          }}
          onClick={() => {
            if (!user) {
              navigate('/sign-in');
              onClose();
            }
          }}
        >
          {user ? t('auth.hiThere') : t('auth.signInSignUp')}
        </Typography>

        {/* Close Button */}
        <IconButton onClick={onClose} size="small" sx={{ p: 1 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      {/* Left Section - Quick Links */}
      <Box
        sx={{
          flex: 1,
          width: selectedCategory ? '50%' : '60%',
          bgcolor: 'white',
          p: 2,
          pt: 8, // Add top padding to account for header
          display: selectedCategory ? 'none' : 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s ease',
        }}
      >

        {/* Conditional Content */}
        {selectedCategory ? (
          /* Categories List */
          <List sx={{ p: 0, flex: 1 }}>
            {categories.map((category) => {
              const isSelected = selectedCategory?.id === category.id;
              
              return (
                <ListItem key={category.id} sx={{ p: 0, mb: 1 }}>
                  <ListItemButton
                    onClick={() => handleCategoryClick(category)}
                    sx={{
                      borderRadius: 1,
                      bgcolor: isSelected ? '#f0f0f0' : 'transparent',
                      border: isSelected ? '1px solid #e0e0e0' : '1px solid transparent',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                      justifyContent: 'flex-start',
                      position: 'relative',
                    }}
                  >
                    <ListItemText
                      primary={getCategoryName(category, i18n.language)}
                      primaryTypographyProps={{
                        fontSize: '0.95rem',
                        color: '#333',
                        fontWeight: isSelected ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        ) : (
          /* Quick Links */
          <List sx={{ p: 0 }}>
            <ListItem sx={{ p: 0, mb: 1 }}>
              <ListItemButton
                onClick={() => handleQuickLinkClick('brands')}
                sx={{
                  borderRadius: 1,
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <ListItemText
                  primary={t('navigation.brands')}
                  primaryTypographyProps={{
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#333',
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem sx={{ p: 0, mb: 1 }}>
              <ListItemButton
                onClick={() => handleQuickLinkClick('new')}
                sx={{
                  borderRadius: 1,
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <ListItemText
                  primary={t('navigation.new')}
                  primaryTypographyProps={{
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#333',
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem sx={{ p: 0, mb: 1 }}>
              <ListItemButton
                onClick={() => handleQuickLinkClick('sale')}
                sx={{
                  borderRadius: 1,
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <ListItemText
                  primary={t('navigation.sale')}
                  primaryTypographyProps={{
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#333',
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        )}

        {/* Language Selection - Only show when no category selected */}
        {!selectedCategory && (
          <Box sx={{ mt: 'auto', pt: 2 }}>
          <Typography 
            sx={{ 
              fontSize: '0.9rem', 
              fontWeight: 500, 
              color: '#666', 
              mb: 1 
            }}
          >
            {t('common.language')}
          </Typography>
          <Box
            onClick={handleLanguageClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: 20, lineHeight: 1, mr: 1 }}
            >
              {currentLanguage.flag}
            </Typography>
            <Typography
              sx={{ 
                color: '#a98c74', 
                fontWeight: 400, 
                fontSize: 14,
                flex: 1
              }}
            >
              {currentLanguage.name}
            </Typography>
            <ExpandMoreIcon sx={{ color: '#a98c74', fontSize: 18 }} />
          </Box>
          <Menu
            anchorEl={languageAnchorEl}
            open={Boolean(languageAnchorEl)}
            onClose={handleLanguageClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            {languages.map(language => (
              <MenuItem
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                selected={i18n.language === language.code}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Typography component="span" sx={{ fontSize: 18, lineHeight: 1, mr: 1.5 }}>
                  {language.flag}
                </Typography>
                <Typography
                  sx={{ color: '#a98c74', fontWeight: 400, fontSize: 14 }}
                >
                  {language.name}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>
        )}
      </Box>

      {/* Dynamic Categories Section */}
      <Box
        sx={{
          overflow: 'auto',
          display: 'flex',
          height: '100%',
          width: selectedCategory ? '100%' : '40%',
          transition: 'width 0.3s ease',
          pt: 8, // Add top padding to account for header
        }}
      >

        {/* Right Section Content */}
        {selectedCategory ? (
          /* Categories and Subcategories - Full Width Split */
          <>
            {/* Categories - Left 50% */}
            <Box
              sx={{
                width: '50%',
                pr: 1,
                overflow: 'auto',
                bgcolor: '#F8F6F3',
                borderTopLeftRadius: '48px',
                p: 2,
              }}
            >
              <List sx={{ p: 0 }}>
                {categories.map((category) => {
                  const isSelected = selectedCategory?.id === category.id;
                  
                  return (
                    <ListItem key={category.id} sx={{ p: 0, mb: 1 }}>
                      <ListItemButton
                        onClick={() => handleCategoryClick(category)}
                        sx={{
                          borderRadius: 1,
                          bgcolor: isSelected ? '#f0f0f0' : 'transparent',
                          border: isSelected ? '1px solid #e0e0e0' : '1px solid transparent',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                          justifyContent: 'flex-start',
                          position: 'relative',
                        }}
                      >
                        <ListItemText
                          primary={getCategoryName(category, i18n.language)}
                          primaryTypographyProps={{
                            fontSize: '0.95rem',
                            color: '#333',
                            fontWeight: isSelected ? 600 : 400,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>

            {/* Subcategories - Right 50% */}
            <Box
              sx={{
                width: '50%',
                pl: 1,
                overflow: 'auto',
                animation: 'slideIn 0.3s ease',
              }}
            >
            {/* Back Button */}
            <Box sx={{ mb: 2, pt: 1 }}>
              <ListItemButton
                onClick={handleBackToCategories}
                sx={{
                  borderRadius: 1,
                  bgcolor: '#f5f5f5',
                  '&:hover': { bgcolor: '#e0e0e0' },
                  justifyContent: 'flex-start',
                  px: 2,
                }}
              >
                <Typography sx={{ fontSize: '0.9rem', color: '#666', fontWeight: 500 }}>
                  ← {t('common.back')}
                </Typography>
              </ListItemButton>
            </Box>

            {/* Subcategories List */}
            <List sx={{ bgcolor: '#FFFFFF', borderRadius: 2, p: 1 }}>
              {subcategories.map((subcategory) => (
                <ListItem key={subcategory.id} sx={{ p: 0, mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleSubcategoryClick(subcategory)}
                    sx={{
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                      justifyContent: 'flex-start',
                      px: 2,
                    }}
                  >
                    <ListItemText
                      primary={getCategoryName(subcategory, i18n.language)}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        color: '#333',
                        fontWeight: 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            </Box>
          </>
        ) : (
          /* Categories - Only show when no subcategories selected */
          <Box
            sx={{
              width: '100%',
              transition: 'width 0.3s ease',
              overflow: 'auto',
            }}
          >
            {isLoading ? (
              <Box sx={{ p: 0 }}>
                {[...Array(8)].map((_, index) => (
                  <Box key={index} sx={{ mb: 1 }}>
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height={48}
                      sx={{ 
                        borderRadius: 1,
                        bgcolor: index % 2 === 0 ? '#FFFFFF' : '#F5F5F5',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <List sx={{ bgcolor: '#F8F6F3', borderTopLeftRadius: '48px', height: '100%' }}>
                {categories.map((category) => {
                  return (
                    <ListItem key={category.id} sx={{ p: 0, mb: 1 }}>
                      <ListItemButton
                        onClick={() => handleCategoryClick(category)}
                        sx={{
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                          justifyContent: 'center',
                          position: 'relative',
                        }}
                      >
                        <ListItemText
                          primary={getCategoryName(category, i18n.language)}
                          primaryTypographyProps={{
                            fontSize: '0.95rem',
                            color: '#333',
                            fontWeight: 400,
                          }}
                          sx={{ textAlign: 'center' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
